import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/utils/authUtils';
import {
  User, Mail, ArrowLeft, Settings, Check, X, Pencil, Sparkles,
  Zap, UserPlus, Share2, Copy,
} from 'lucide-react';
import { PixelAvatar, AvatarSelector, UserAvatar } from '@/components/PixelAvatar';
import { DEFAULT_AVATAR } from '@/data/avatars';
import { apiFetch } from '@/hooks/useFetch';
import { Progress } from '@/components/ui/progress';
import { StreakGoalCard } from '@/components/StreakGoalCard';
import { SavedRephrasesCard } from '@/components/SavedRephrasesCard';
import ContributorRequestForm from '@/components/ContributorRequestForm';
import AccountBadgesCard from '@/components/account/AccountBadgesCard';
import AccountAuthorBioCard from '@/components/account/AccountAuthorBioCard';


/**
 * ProfilePage (/profile) — public-facing identity + gamification.
 * Everything another user could see lives here. Private config lives at /settings.
 */
const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  const { isSpanish } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === '1';

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(DEFAULT_AVATAR);
  const [displayNameError, setDisplayNameError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);

  const [bio, setBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [authorDisplayName, setAuthorDisplayName] = useState('');
  const [authorPhoto, setAuthorPhoto] = useState('');
  const [authorCredentials, setAuthorCredentials] = useState('');

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setSelectedAvatar(user.avatar || DEFAULT_AVATAR);
      setBio(user.bio || '');
      setAuthorDisplayName(user.author_display_name || '');
      setAuthorPhoto(user.author_photo || '');
      setAuthorCredentials(user.author_credentials || '');
    }
  }, [user]);

  useEffect(() => {
    if (isWelcome && user && !isEditingProfile) {
      setShowWelcomeBanner(true);
      setIsEditingProfile(true);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWelcome, user?.user_id]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const dismissWelcomeBanner = () => {
    setShowWelcomeBanner(false);
    setSearchParams({}, { replace: true });
  };

  const checkDisplayName = useCallback(async (name) => {
    if (!name || name.length < 2) { setDisplayNameError(name ? 'Display name must be at least 2 characters' : ''); return; }
    if (name.length > 30) { setDisplayNameError('Display name must be 30 characters or less'); return; }
    if (name === user?.display_name) { setDisplayNameError(''); return; }
    setIsCheckingName(true);
    try {
      const { data, error } = await apiFetch(`/api/auth/check-display-name/${encodeURIComponent(name)}`);
      if (!error && data) setDisplayNameError(data.available ? '' : data.reason);
      else setDisplayNameError('');
    } catch { setDisplayNameError(''); }
    finally { setIsCheckingName(false); }
  }, [user?.display_name]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (displayName && isEditingProfile) checkDisplayName(displayName);
    }, 500);
    return () => clearTimeout(timer);
  }, [displayName, isEditingProfile, checkDisplayName]);

  const handleSaveProfile = async () => {
    if (displayNameError) return;
    setIsSavingProfile(true);
    try {
      const updates = {};
      if (displayName && displayName !== user?.display_name) updates.display_name = displayName;
      if (selectedAvatar !== user?.avatar) updates.avatar = selectedAvatar;
      if (Object.keys(updates).length === 0) { setIsEditingProfile(false); return; }
      const { error } = await apiFetch('/api/auth/profile', { method: 'PUT', body: updates });
      if (!error) {
        toast.success(isSpanish ? 'Perfil actualizado' : 'Profile updated successfully');
        setIsEditingProfile(false);
        if (isWelcome) { setShowWelcomeBanner(false); setSearchParams({}, { replace: true }); }
        if (refreshUser) refreshUser();
      } else {
        toast.error(error || 'Failed to update profile');
      }
    } catch { toast.error('Something went wrong'); }
    finally { setIsSavingProfile(false); }
  };

  const handleSaveBio = async () => {
    setIsSavingBio(true);
    try {
      const { error } = await apiFetch('/api/auth/profile', {
        method: 'PUT',
        body: {
          bio: bio.trim(),
          author_display_name: authorDisplayName.trim(),
          author_photo: authorPhoto.trim(),
          author_credentials: authorCredentials.trim(),
        },
      });
      if (error) toast.error(error || (isSpanish ? 'No se pudo guardar' : 'Could not save bio'));
      else { toast.success(isSpanish ? 'Biografía actualizada' : 'Bio updated'); if (refreshUser) refreshUser(); }
    } catch { toast.error(isSpanish ? 'Error al guardar' : 'Failed to save'); }
    finally { setIsSavingBio(false); }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container py-6 max-w-2xl mx-auto px-4 pb-20">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isSpanish ? 'Atrás' : 'Back'}
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">{isSpanish ? 'Mi Perfil' : 'My Profile'}</h1>
          <Link to="/settings" className="text-sm text-[#00A896] hover:underline inline-flex items-center gap-1" data-testid="link-to-settings">
            <Settings className="h-4 w-4" />
            {isSpanish ? 'Configuración' : 'Settings'}
          </Link>
        </div>

        {showWelcomeBanner && (
          <Card data-testid="welcome-first-friend-banner" className="mb-6 border-2 border-[#00E5CC]/40 bg-gradient-to-br from-[#00E5CC]/10 via-purple-500/5 to-[#00A896]/10 shadow-md">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-[#00E5CC] to-[#00A896] flex items-center justify-center shadow-md">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <UserPlus className="h-4 w-4 text-[#00A896]" />
                    <p className="font-semibold text-base text-foreground">
                      {isSpanish ? '¡Tienes tu primer amigo!' : 'You have your first friend!'}
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">Katie</span>
                    {isSpanish
                      ? ', la fundadora de la app, ¡ya está conectada contigo!'
                      : ', the app founder, is already connected with you!'}
                  </p>
                </div>
                <button type="button" onClick={dismissWelcomeBanner} data-testid="welcome-banner-dismiss" aria-label="Dismiss" className="shrink-0 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted/50 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6" data-testid="profile-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {isSpanish ? 'Información del Perfil' : 'Profile Information'}
              </CardTitle>
              {!isEditingProfile && (
                <Button variant="ghost" size="sm" onClick={() => setIsEditingProfile(true)} data-testid="edit-profile-btn">
                  <Pencil className="h-4 w-4 mr-1" />
                  {isSpanish ? 'Editar' : 'Edit'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditingProfile ? (
              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium mb-3 block">{isSpanish ? 'Elige tu Avatar' : 'Choose Your Avatar'}</Label>
                  <AvatarSelector selectedId={selectedAvatar} onSelect={setSelectedAvatar} size={48} isSpanish={isSpanish} user={user} />
                </div>
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <PixelAvatar avatarId={selectedAvatar} size={56} />
                  <div>
                    <p className="text-sm text-muted-foreground">{isSpanish ? 'Vista previa' : 'Preview'}</p>
                    <p className="font-medium">{displayName || user.name}</p>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label htmlFor="display-name">{isSpanish ? 'Nombre para Mostrar (Único)' : 'Display Name (Unique)'}</Label>
                  <div className="flex-1 relative">
                    <Input id="display-name" placeholder={isSpanish ? 'TuNombreÚnico' : 'YourUniqueName'} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={30} className={displayNameError ? 'border-red-500' : displayName && !isCheckingName ? 'border-green-500' : ''} />
                    {isCheckingName && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div></div>}
                    {!isCheckingName && displayName && !displayNameError && <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                    {!isCheckingName && displayNameError && <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
                  </div>
                  {displayNameError && <p className="text-xs text-red-500">{displayNameError}</p>}
                  <p className="text-xs text-muted-foreground">{isSpanish ? 'Aparece en las tablas de clasificación. Sin apellidos, por favor.' : 'This name appears on leaderboards. No last names, please.'}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveProfile} disabled={isSavingProfile || !!displayNameError || isCheckingName}>
                    {isSavingProfile ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Guardar' : 'Save')}
                  </Button>
                  <Button variant="outline" onClick={() => { setIsEditingProfile(false); setDisplayName(user.display_name || ''); setSelectedAvatar(user.avatar || DEFAULT_AVATAR); setDisplayNameError(''); }}>
                    {isSpanish ? 'Cancelar' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <UserAvatar user={user} size={64} />
                  <div>
                    <p className="font-semibold text-lg">{user.name}</p>
                    {user.display_name && <p className="text-sm text-[#00E5CC]">@{user.display_name}</p>}
                    <p className="text-muted-foreground flex items-center gap-1 text-sm"><Mail className="h-3 w-3" />{user.email}</p>
                  </div>
                </div>
                {!user.display_name && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 dark:bg-yellow-950/30 dark:border-yellow-900">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">{isSpanish ? '⚠️ Configura un nombre para mostrar para aparecer en las tablas de clasificación.' : '⚠️ Set a display name to appear on leaderboards.'}</p>
                  </div>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{isSpanish ? 'Frases Distintas Vistas' : 'Different Phrases Viewed'}</p>
                    <p className="font-medium">{user.phrases_viewed || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{isSpanish ? 'Racha Actual' : 'Current Streak'}</p>
                    <p className="font-medium">{user.current_streak || 0} 🔥</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6" data-testid="xp-level-card">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2"><Zap className="h-5 w-5 text-yellow-500" /><span className="font-semibold">{isSpanish ? 'Nivel' : 'Level'} {user.level || 1}</span></div>
              <span className="text-sm text-muted-foreground">{user.total_xp || 0} XP</span>
            </div>
            <Progress value={((user.total_xp || 0) % 100)} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1">{100 - ((user.total_xp || 0) % 100)} XP {isSpanish ? 'para el siguiente nivel' : 'to next level'}</p>
          </CardContent>
        </Card>

        <StreakGoalCard />
        <SavedRephrasesCard />

        <AccountBadgesCard user={user} isSpanish={isSpanish} />

        <Card className="mb-6" data-testid="referral-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Share2 className="h-5 w-5 text-primary" />{isSpanish ? '¡Invita a un Amigo!' : 'Invite a Friend!'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input value={user.referral_code || user.display_name || ''} readOnly className="text-sm font-mono" />
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(user.referral_code || user.display_name || ''); toast.success(isSpanish ? 'Copiado' : 'Copied!'); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{isSpanish ? 'Comparte tu código. Cuando un amigo se suscribe a VIP, ambos reciben 1 mes gratis.' : 'Share your code. When a friend subscribes to VIP, you both get 1 month free.'}</p>
          </CardContent>
        </Card>

        {(user?.is_admin || user?.is_contributor) && (
          <AccountAuthorBioCard
            user={user}
            isSpanish={isSpanish}
            authorDisplayName={authorDisplayName}
            setAuthorDisplayName={setAuthorDisplayName}
            authorPhoto={authorPhoto}
            setAuthorPhoto={setAuthorPhoto}
            authorCredentials={authorCredentials}
            setAuthorCredentials={setAuthorCredentials}
            bio={bio}
            setBio={setBio}
            onSave={handleSaveBio}
            isSavingBio={isSavingBio}
          />
        )}

        {/* Contributor request — only for users who aren't already
            admin/contributor. Hidden once promoted. */}
        {!user?.is_admin && !user?.is_contributor && (
          <ContributorRequestForm isSpanish={isSpanish} />
        )}

        <div className="text-center mt-2 mb-2">
          <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-to-settings">
            <Settings className="h-4 w-4" />
            {isSpanish ? 'Configuración avanzada y suscripción →' : 'Account settings & subscription →'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
